"use client";

/**
 * Confirmation modal for the unified sync flow. Rendered by
 * `SyncFlowProvider` so it's always mounted; visibility is driven by
 * `useSyncFlowContext().isOpen`. Two-panel branded shell (colored sidebar +
 * content panel) matches the export/enrollment/request-integration wizards.
 * Content (cost breakdown, before -> after balance, insufficient banner,
 * per-candidate contact enrichment) mirrors the retired promote modal.
 */

import * as React from "react";
import NumberFlow from "@number-flow/react";

import {
  Modal,
  ModalClose,
  ModalContent,
  ModalTitle,
} from "@/components/ui/modal";
import { Button, SubmitButton } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Select } from "@/components/ui/select";
import { Icon } from "@/components/ui/icon";
import { pluralize } from "@/lib/string/pluralize";
import { cn } from "@/lib/utils";
import type { SyncableView } from "@/lib/api/model";

import { useSyncFlowContext } from "./use-sync-flow";
import {
  hardBlockReason,
  isFillableApplicationBlock,
  showFillAnswersCta,
} from "../../lib/hard-block-reason";
import { useJobAtsLink } from "../../hooks/use-job-ats-link";
import { useConnectionSyncSettings } from "../../hooks/use-sync-settings";
import { ApplicationAnswersSheet } from "./application-answers-sheet";

/** Synthetic placeholder addresses (`{profile_id}@arbi.email`) read as
 * "Placeholder" rather than exposing the internal uuid-based email. */
function displayEmail(address: string): string {
  return address.endsWith("@arbi.email") ? "Placeholder" : address;
}

const INELIGIBLE_COPY = {
  already_synced: "Already synced",
  sync_in_progress: "Sync in progress",
  in_sourced: "Still in Sourced",
} as const;

const SOFT_APPLICATION_QUESTIONS_COPY =
  "This job has application questions — fill in Arbi if you want them on the ATS application";

const SIDEBAR_FEATURES = [
  "Review the credit cost first",
  "Choose who gets contact info",
  "Resumes attach automatically",
  "See what's blocked and why",
];

export function SyncConfirmModal() {
  const {
    isOpen,
    close,
    jobId,
    jobCandidateIds,
    names,
    preflight,
    preflightLoading,
    preflightError,
    submitting,
    insufficient,
    submit,
    refetchPreflight,
  } = useSyncFlowContext();

  const { connectionId } = useJobAtsLink(jobId);
  const { autoEnrichOnAutoSync, isLoading: syncSettingsLoading } =
    useConnectionSyncSettings(connectionId);

  const [emailByCandidate, setEmailByCandidate] = React.useState<
    Record<string, string | null>
  >({});
  const [enrichByCandidate, setEnrichByCandidate] = React.useState<
    Record<string, boolean>
  >({});
  const [ackHardBlocked, setAckHardBlocked] = React.useState(false);
  const [expanded, setExpanded] = React.useState(false);
  const [ineligibleExpanded, setIneligibleExpanded] = React.useState(false);
  // The preflight response whose auto-enrich default has already been
  // seeded into enrichByCandidate — null until seeded, so a fresh preflight
  // (identity change) can seed again exactly once.
  const [seededFor, setSeededFor] = React.useState<unknown>(null);
  const [answersTargetId, setAnswersTargetId] = React.useState<string | null>(
    null,
  );

  // Reset local state when the modal transitions from open -> closed. This
  // adjusts state during render (React's recommended pattern for deriving
  // state from a prop change) rather than in an effect, avoiding an extra
  // cascading render.
  const [prevIsOpen, setPrevIsOpen] = React.useState(isOpen);
  if (isOpen !== prevIsOpen) {
    setPrevIsOpen(isOpen);
    if (!isOpen) {
      setEmailByCandidate({});
      setEnrichByCandidate({});
      setAckHardBlocked(false);
      setExpanded(false);
      setIneligibleExpanded(false);
      setSeededFor(null);
      setAnswersTargetId(null);
    }
  }

  const pf = preflight;
  const ats = pf?.ats_display_name ?? "your ATS";
  const syncable = pf?.syncable ?? [];
  const hardBlocked = pf?.hard_blocked ?? [];
  const ineligible = pf?.ineligible ?? [];
  const answersTarget = answersTargetId
    ? [...syncable, ...hardBlocked, ...ineligible].find(
        (candidate) => candidate.job_candidate_id === answersTargetId,
      )
    : undefined;
  const fillableBlockedIds = hardBlocked
    .filter((h) => isFillableApplicationBlock(h.block_codes))
    .map((h) => h.job_candidate_id);
  const eligibleForEnrich = syncable
    .filter((c) => c.enrichment_available)
    .map((c) => c.job_candidate_id);

  // Once preflight and the connection's auto-enrich setting have both
  // loaded, seed the bulk toggle to on if the connection auto-enriches — a
  // default, not a lock, so later manual toggling isn't touched by this.
  // Adjusting state during render (comparing against `seededFor`) instead of
  // an effect avoids an extra cascading render.
  if (pf && !syncSettingsLoading && seededFor !== pf) {
    setSeededFor(pf);
    if (autoEnrichOnAutoSync && eligibleForEnrich.length > 0) {
      setEnrichByCandidate((prev) => {
        const next = { ...prev };
        eligibleForEnrich.forEach((id) => {
          next[id] = true;
        });
        return next;
      });
    }
  }

  const bulkEnrichChecked =
    eligibleForEnrich.length > 0 &&
    eligibleForEnrich.every((id) => enrichByCandidate[id]);
  const bulkEnrichIndeterminate =
    !bulkEnrichChecked && eligibleForEnrich.some((id) => enrichByCandidate[id]);

  function handleBulkEnrichChange(checked: boolean) {
    setEnrichByCandidate((prev) => {
      const next = { ...prev };
      eligibleForEnrich.forEach((id) => {
        next[id] = checked;
      });
      return next;
    });
  }

  const enrichCount = syncable.filter(
    (c) => enrichByCandidate[c.job_candidate_id],
  ).length;
  const syncCost = pf
    ? syncable.length * pf.costs.sync_credit_per_candidate
    : 0;
  const enrichCost = pf
    ? enrichCount * pf.costs.enrich_credit_per_candidate
    : 0;
  const total = syncCost + enrichCost;
  const balance = pf?.balance ?? 0;
  const requestedCount = pf?.resolved_count ?? jobCandidateIds.length;
  const notEnough = insufficient || (!!pf && balance < total);
  const needsAck = hardBlocked.length > 0;
  const confirmDisabled =
    preflightLoading ||
    preflightError ||
    !pf ||
    submitting ||
    syncable.length === 0 ||
    notEnough ||
    (needsAck && !ackHardBlocked);

  return (
    <>
    <Modal
      open={isOpen}
      dismissible={!submitting}
      onOpenChange={(open) => (open || submitting ? undefined : close())}
    >
      <ModalContent size="lg" className="max-h-[92dvh] p-0 sm:max-w-180">
        <div className="sr-only">
          <ModalTitle>{`Sync to ${ats}`}</ModalTitle>
        </div>
        <div className="relative flex max-h-[92dvh] min-h-0 flex-col overflow-hidden sm:h-140 sm:flex-row">
          {/* ── Left branded sidebar ── */}
          <div className="hidden w-[280px] shrink-0 flex-col justify-between border-r border-border bg-[#7DD3FC] px-7 py-8 sm:flex">
            <div className="flex flex-col gap-4">
              <div className="flex items-center gap-2">
                <Icon name="IconCloudSync" size={22} className="text-black" />
                <span className="text-base font-semibold text-black">Sync</span>
              </div>
              <div className="mt-4 flex flex-col gap-2">
                <h2 className="text-2xl font-semibold leading-tight text-black">
                  Sync {requestedCount}{" "}
                  {pluralize(requestedCount, "profile")} to {ats}.
                </h2>
                <p className="text-[13px] leading-relaxed text-black/60">
                  Review the credit cost and contact info before syncing to{" "}
                  {ats}.
                </p>
              </div>
            </div>
            <div className="mt-8 flex flex-col gap-3">
              {SIDEBAR_FEATURES.map((f) => (
                <div key={f} className="flex items-center gap-2.5">
                  <div className="flex size-5 shrink-0 items-center justify-center rounded-full bg-black/10">
                    <Icon
                      name="IconCheckmark1"
                      size={12}
                      className="text-black"
                    />
                  </div>
                  <span className="text-[13px] text-black/80">{f}</span>
                </div>
              ))}
            </div>
          </div>

          {/* ── Right content panel ── */}
          <div className="relative flex min-h-0 flex-1 flex-col bg-surface-elevated">
            {!submitting && (
              <ModalClose asChild>
                <button
                  type="button"
                  className="absolute right-3.5 top-3.5 z-10 flex size-6 cursor-pointer items-center justify-center rounded-md transition-colors hover:bg-contrast/6"
                >
                  <Icon
                    name="IconCrossMedium"
                    size={14}
                    className="text-contrast/40 hover:text-contrast/70"
                  />
                </button>
              </ModalClose>
            )}

            <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-5 py-5 sm:px-6 sm:py-6">
              {preflightError ? (
                    <div className="flex items-center gap-2.5 rounded-lg border border-border bg-contrast/2 px-3 py-2.5">
                      <Icon
                        name="IconExclamationCircle"
                        className="size-4 text-destructive"
                      />
                      <span className="text-xs text-muted-foreground">
                        Couldn&apos;t load the sync details. Close and try
                        again.
                      </span>
                    </div>
                  ) : preflightLoading || !pf ? (
                    <div className="h-24 animate-pulse rounded-lg bg-foreground/4" />
                  ) : (
                    <>
                      {/* Cost breakdown */}
                      <div className="flex flex-col gap-1 rounded-lg border border-border bg-contrast/[0.02] p-2.5 text-[13px]">
                        <div className="flex items-center justify-between">
                          <span className="text-muted-foreground">
                            Profiles to sync
                          </span>
                          <span className="font-medium text-foreground tabular-nums">
                            {syncable.length}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-muted-foreground">
                            Sync credits ({syncable.length} &times;{" "}
                            {pf.costs.sync_credit_per_candidate})
                          </span>
                          <span className="font-medium text-foreground tabular-nums">
                            {syncCost}
                          </span>
                        </div>
                        {enrichCount > 0 && (
                          <div className="flex items-center justify-between">
                            <span className="text-muted-foreground">
                              Contact enrichment ({enrichCount} &times;{" "}
                              {pf.costs.enrich_credit_per_candidate})
                            </span>
                            <span className="font-medium text-foreground tabular-nums">
                              {enrichCost}
                            </span>
                          </div>
                        )}
                        <div className="mt-0.5 flex items-center justify-between border-t border-border pt-1.5">
                          <span className="font-medium text-foreground">
                            Total
                          </span>
                          <span className="font-semibold text-destructive tabular-nums">
                            {total}
                          </span>
                        </div>
                      </div>

                      {eligibleForEnrich.length > 0 && (
                        <div className="flex flex-col gap-1">
                          <label className="flex items-center gap-2 text-xs">
                            <Checkbox
                              checked={bulkEnrichChecked}
                              indeterminate={bulkEnrichIndeterminate}
                              onChange={handleBulkEnrichChange}
                              aria-label="Include contact info for all profiles"
                            />
                            <span className="text-foreground">
                              Include contact info for{" "}
                              {eligibleForEnrich.length}{" "}
                              {pluralize(eligibleForEnrich.length, "profile")}{" "}
                              (+
                              {pf.costs.enrich_credit_per_candidate} credits
                              each)
                            </span>
                          </label>
                          {autoEnrichOnAutoSync && (
                            <span className="pl-6 text-xs text-muted-foreground">
                              Auto-enrich is on for this connection
                            </span>
                          )}
                        </div>
                      )}

                      {syncable.length > 0 && (
                        <p className="text-xs text-muted-foreground">
                          Their resume or profile document will be attached to
                          the ATS record.
                        </p>
                      )}

                      {/* Balance — before -> after */}
                      <div className="flex items-center justify-between rounded-lg border border-border bg-contrast/2 px-3 py-2.5">
                        <span className="text-xs font-medium text-muted-foreground">
                          Changes to your balance
                        </span>
                        <span className="flex items-center gap-2 text-sm">
                          <span className="flex items-center gap-1 text-muted-foreground">
                            <NumberFlow value={balance} />
                          </span>
                          <Icon
                            name="IconArrowRight"
                            size={13}
                            className="text-foreground opacity-40"
                          />
                          <span
                            className={cn(
                              "flex items-center gap-1 font-semibold",
                              notEnough ? "text-destructive" : "text-heading",
                            )}
                          >
                            <NumberFlow value={Math.max(balance - total, 0)} />
                          </span>
                        </span>
                      </div>

                      {notEnough && (
                        <div className="flex items-center gap-2.5 rounded-lg border border-border bg-contrast/2 px-3 py-2.5">
                          <div className="flex flex-col gap-0.5">
                            <span className="flex items-center gap-1 text-xs font-medium text-destructive">
                              <Icon
                                name="IconExclamationCircle"
                                className="size-4"
                              />
                              Not enough credits
                            </span>
                            <span className="text-xs text-muted-foreground">
                              You need{" "}
                              <b className="font-medium text-foreground">
                                {Math.max(total - balance, 0)} more{" "}
                                {pluralize(
                                  Math.max(total - balance, 0),
                                  "credit",
                                )}
                              </b>{" "}
                              to sync.
                            </span>
                          </div>
                        </div>
                      )}

                      {/* Expandable per-candidate review */}
                      {syncable.length > 0 && (
                        <button
                          type="button"
                          onClick={() => setExpanded((prev) => !prev)}
                          className="w-fit text-left text-xs text-muted-foreground underline"
                        >
                          {expanded ? "Hide" : "Review"} {syncable.length}{" "}
                          {pluralize(syncable.length, "profile")}
                        </button>
                      )}
                      {expanded && (
                        <div className="flex flex-col gap-2">
                          {syncable.map((candidate) => (
                            <CandidateRow
                              key={candidate.job_candidate_id}
                              candidate={candidate}
                              label={
                                candidate.full_name ??
                                names[candidate.job_candidate_id] ??
                                candidate.job_candidate_id
                              }
                              value={
                                emailByCandidate[candidate.job_candidate_id] ??
                                candidate.auto_pick ??
                                null
                              }
                              onChange={(value) =>
                                setEmailByCandidate((prev) => ({
                                  ...prev,
                                  [candidate.job_candidate_id]: value,
                                }))
                              }
                              enrichUnit={pf.costs.enrich_credit_per_candidate}
                              enrichChecked={
                                !!enrichByCandidate[candidate.job_candidate_id]
                              }
                              onEnrichChange={(checked) =>
                                setEnrichByCandidate((prev) => ({
                                  ...prev,
                                  [candidate.job_candidate_id]: checked,
                                }))
                              }
                              showFillAnswers={
                                candidate.application_questions_soft_hint ===
                                true
                              }
                              onFillAnswers={() =>
                                setAnswersTargetId(candidate.job_candidate_id)
                              }
                            />
                          ))}
                        </div>
                      )}

                      {/* Hard-blocked group + acknowledgement */}
                      {hardBlocked.length > 0 && (
                        <div className="flex flex-col gap-2 rounded-lg border border-border bg-contrast/2 p-2.5">
                          <span className="text-xs font-medium text-muted-foreground">
                            {hardBlocked.length}{" "}
                            {pluralize(hardBlocked.length, "profile")}{" "}
                            can&apos;t be synced
                          </span>
                          {hardBlocked.map((h) => {
                            const showFillAnswers = showFillAnswersCta({
                              softHint: h.application_questions_soft_hint,
                              blockCodes: h.block_codes,
                            });
                            return (
                              <div
                                key={h.job_candidate_id}
                                className="flex flex-col gap-1"
                              >
                                <span className="text-xs font-medium text-foreground">
                                  {h.full_name ??
                                    names[h.job_candidate_id] ??
                                    h.job_candidate_id}
                                </span>
                                <p className="text-xs text-muted-foreground">
                                  {hardBlockReason(
                                    h.block_codes ?? [],
                                    h.missing_fields ?? [],
                                    ats,
                                  )}
                                </p>
                                {showFillAnswers ? (
                                  <button
                                    type="button"
                                    className="w-fit text-left text-xs font-medium text-primary underline"
                                    onClick={() =>
                                      setAnswersTargetId(h.job_candidate_id)
                                    }
                                  >
                                    Fill answers
                                  </button>
                                ) : null}
                              </div>
                            );
                          })}
                          <label className="flex items-center gap-2 text-xs">
                            <Checkbox
                              checked={ackHardBlocked}
                              onChange={setAckHardBlocked}
                            />
                            I understand {hardBlocked.length}{" "}
                            {pluralize(hardBlocked.length, "profile")}{" "}
                            can&apos;t be synced and will stay in Arbi only.
                          </label>
                        </div>
                      )}

                      {/* Ineligible group — muted, excluded from the count, collapsed
                  by default so a long already-synced list doesn't dominate
                  the modal above the actionable candidates. */}
                      {ineligible.length > 0 && (
                        <div className="flex flex-col gap-1.5 rounded-lg border border-border bg-contrast/2 p-2.5">
                          <button
                            type="button"
                            onClick={() =>
                              setIneligibleExpanded((prev) => !prev)
                            }
                            className="flex items-center justify-between text-xs font-medium text-muted-foreground"
                          >
                            <span>Not included ({ineligible.length})</span>
                            <Icon
                              name="IconChevronDownSmall"
                              size={12}
                              className={cn(
                                "transition-transform",
                                ineligibleExpanded && "rotate-180",
                              )}
                            />
                          </button>
                          {ineligibleExpanded &&
                            ineligible.map((i) => (
                              <div
                                key={i.job_candidate_id}
                                className="flex items-center justify-between gap-2 text-xs text-muted-foreground"
                              >
                                <span className="truncate">
                                  {i.full_name ??
                                    names[i.job_candidate_id] ??
                                    i.job_candidate_id}
                                </span>
                                <span>
                                  {INELIGIBLE_COPY[
                                    i.reason as keyof typeof INELIGIBLE_COPY
                                  ] ?? i.reason}
                                </span>
                              </div>
                            ))}
                        </div>
                      )}
                    </>
                  )}
                </div>

            <div className="flex shrink-0 items-center justify-end gap-3 border-t border-border px-5 py-3 sm:px-6">
              <ModalClose asChild>
                <Button variant="secondary" size="sm" disabled={submitting}>
                  Cancel
                </Button>
              </ModalClose>
              <SubmitButton
                type="button"
                variant="primary"
                size="sm"
                disabled={confirmDisabled}
                loading={submitting}
                onClick={() =>
                  submit({ enrichByCandidate, emailByCandidate })
                }
              >
                {`Sync ${syncable.length}`}
              </SubmitButton>
            </div>
          </div>
        </div>
      </ModalContent>
    </Modal>
      {jobId && answersTargetId ? (
        <ApplicationAnswersSheet
          open
          onOpenChange={(next) => {
            if (!next) setAnswersTargetId(null);
          }}
          jobId={jobId}
          jobCandidateId={answersTargetId}
          candidateName={
            answersTarget?.full_name ??
            names[answersTargetId] ??
            answersTargetId
          }
          blockedJobCandidateIds={fillableBlockedIds}
          onSaved={() => {
            void refetchPreflight();
          }}
        />
      ) : null}
    </>
  );
}

function CandidateRow({
  candidate,
  label,
  value,
  onChange,
  enrichUnit,
  enrichChecked,
  onEnrichChange,
  showFillAnswers,
  onFillAnswers,
}: {
  candidate: SyncableView;
  label: string;
  value: string | null;
  onChange: (value: string) => void;
  enrichUnit: number;
  enrichChecked: boolean;
  onEnrichChange: (checked: boolean) => void;
  showFillAnswers: boolean;
  onFillAnswers: () => void;
}) {
  const [open, setOpen] = React.useState(false);
  const emails = candidate.emails ?? [];
  const selected = emails.find((e) => e.address === value) ?? null;

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center justify-between gap-2 text-sm">
        <span className="truncate">{label}</span>
        {emails.length > 0 ? (
          <Select.Root open={open} onOpenChange={setOpen}>
            <Select.Trigger variant="modal" className="w-48">
              <span className="truncate text-sm text-heading">
                {selected ? displayEmail(selected.address) : "Select an email"}
              </span>
              <Select.Icon className="ml-auto" />
            </Select.Trigger>
            <Select.Content>
              <Select.List>
                {emails.map((email, index) => (
                  <Select.Item
                    key={email.address}
                    index={index}
                    selected={email.address === value}
                    onSelect={() => {
                      onChange(email.address);
                      setOpen(false);
                    }}
                  >
                    <Select.ItemLabel selected={email.address === value}>
                      {displayEmail(email.address)}
                    </Select.ItemLabel>
                    {email.address === value && <Select.ItemCheck />}
                  </Select.Item>
                ))}
              </Select.List>
            </Select.Content>
          </Select.Root>
        ) : (
          <span className="text-xs text-muted-foreground">
            No email on file
          </span>
        )}
      </div>
      {showFillAnswers && (
        <div className="flex flex-col gap-1">
          <p className="text-xs text-muted-foreground">
            {SOFT_APPLICATION_QUESTIONS_COPY}
          </p>
          <button
            type="button"
            className="w-fit text-left text-xs font-medium text-primary underline"
            onClick={onFillAnswers}
          >
            Fill answers
          </button>
        </div>
      )}
      {candidate.enrichment_available && (
        <label className="flex items-center gap-2 text-xs">
          <Checkbox
            checked={enrichChecked}
            onChange={onEnrichChange}
            aria-label={`Find contact info (+${enrichUnit} credits)`}
          />
          Find contact info (+{enrichUnit} credits)
        </label>
      )}
      {candidate.will_use_placeholder && !enrichChecked && (
        <span className="text-xs text-muted-foreground">
          Will use a placeholder email
        </span>
      )}
    </div>
  );
}
